import React from 'react'
import styled, { css } from 'styled-components'
import { Colors, Typography } from 'cio-design-library/design-tokens'
import Media from 'builder/styles/media'
import {
  GroupControl as GroupControlComponent,
  GroupControlTitle,
} from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/styles'

export const Container = styled.div`
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  height: 100%;

  ${Media.Tablet`
    height: unset;
    padding: 32px 40px 94px;
  `}

  ${Media.Phone`
    padding: 24px 16px 94px;
  `}
`

/** Style overrides for layout input controls */
export const SelectGroup = styled.div`
  position: relative;
  display: flex;
`

const DefaultBorderPositions = {
  left: css`
    border-left: 1px solid ${Colors.Neutral15};
    border-right: 1px solid ${Colors.Neutral15};
    border-top: 1px solid ${Colors.Neutral15};
    border-bottom: 1px solid ${Colors.Neutral15};
    border-radius: 8px 0 0 8px;
  `,
  center: css`
    border-right: 1px solid ${Colors.Neutral15};
    border-top: 1px solid ${Colors.Neutral15};
    border-bottom: 1px solid ${Colors.Neutral15};
  `,
  right: css`
    border-right: 1px solid ${Colors.Neutral15};
    border-top: 1px solid ${Colors.Neutral15};
    border-bottom: 1px solid ${Colors.Neutral15};
    border-radius: 0 8px 8px 0;
  `,
}
const ActiveBorderPositions = {
  left: css`
    border-left: 1px solid ${Colors.Blue20};
    border-right: 1px solid ${Colors.Blue20} !important;
    border-top: 1px solid ${Colors.Blue20};
    border-bottom: 1px solid ${Colors.Blue20};
    border-radius: 8px 0 0 8px;
  `,
  center: css`
    border-left: 1px solid ${Colors.Blue20};
    border-right: 1px solid ${Colors.Blue20} !important;
    border-top: 1px solid ${Colors.Blue20};
    border-bottom: 1px solid ${Colors.Blue20};
  `,
  right: css`
    border-right: 1px solid ${Colors.Blue20};
    border-left: 1px solid ${Colors.Blue20};
    border-top: 1px solid ${Colors.Blue20};
    border-bottom: 1px solid ${Colors.Blue20};
    border-radius: 0 8px 8px 0;
  `,
}

export const InnerSelectControl = styled.div<{
  position: 'left' | 'center' | 'right'
  $height?: number
}>`
  width: 100%;
  height: ${({ $height }) => $height ?? 80}px;
  padding: 20px 24px;
  border: 1px solid transparent;
  background: transparent;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;

  &:not(:has(input:checked)) {
    ${({ position }) => DefaultBorderPositions[position]}
  }

  &:has(input:checked) {
    ${({ position }) => ActiveBorderPositions[position]}
    background: ${Colors.Blue10};
  }

  &:has(input:disabled) {
    background: ${Colors.Neutral5};
  }

  & input[type='radio'] {
    display: none;
  }
`
export const SelectControlWraper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:not(:has(input:disabled)) {
    label {
      cursor: pointer;
    }
  }

  label {
    color: ${Colors.Neutral50};

    svg {
      max-height: 100% !important;
      display: block;
    }

    /* DEFAULT STATE - Neutral colors */

    /* For SVGs WITHOUT gradients (columns, categories) */
    svg:not(:has(defs)) rect {
      fill: ${Colors.Neutral10};
      fill-opacity: 1;
    }

    svg:not(:has(defs)) path {
      fill: ${Colors.Neutral10};
      fill-opacity: 1;
    }

    /* For SVGs WITH gradients */
    /* The gradient rectangles keep their opacity */
    svg rect[opacity] {
      opacity: 0.2;
    }

    svg path[opacity] {
      /* opacity: 0.2; */
    }

    /* Gradient stops - Neutral30 */
    svg stop {
      stop-color: ${Colors.Neutral30};
      /* Don't override stop-opacity here - let the gradient fade naturally */
    }

    /* Circles and paths with white fill - Neutral10 */
    svg path[fill='white'] {
      fill: ${Colors.Neutral10};
    }

    svg circle[fill='white'] {
      fill: ${Colors.Neutral50};
    }
  }

  &:has(input:checked) {
    label {
      color: ${Colors.Neutral90};

      /* CHECKED STATE - Blue colors */

      /* For SVGs WITHOUT gradients (columns, categories) */
      svg:not(:has(defs)) rect {
        fill: ${Colors.Blue20};
        fill-opacity: 1;
      }

      svg:not(:has(defs)) path {
        fill: ${Colors.Blue20};
        fill-opacity: 1;
      }

      /* For SVGs WITH gradients */
      /* Make gradient rectangles fully visible */
      svg rect[opacity] {
        opacity: 1; /* ← Change from 0.5 to 1 */
      }

      svg path[opacity] {
        opacity: 1; /* ← Change from 0.5 to 1 */
      }

      /* Gradient stops - Blue20 */
      svg stop {
        stop-color: ${Colors.Blue20};
        /* Don't set stop-opacity: 1 - it will break the fade effect */
      }

      /* Circles and paths - Blue20 */
      svg path[fill='white'],
      svg path[fill='${Colors.Neutral10}'] {
        fill: ${Colors.Blue20};
      }

      svg circle[fill='white'],
      svg circle[fill='${Colors.Neutral10}'] {
        fill: ${Colors.Blue20};
      }
    }
  }
`

export const SelectControl = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof SelectControlWraper> & {
    label?: React.ReactNode
    $height?: number
    position: 'left' | 'center' | 'right'
    children: React.ReactNode
  }
>((props, ref) => {
  const { position, $height, label, children, ...rest } = props
  return (
    <SelectControlWraper ref={ref} {...rest}>
      <InnerSelectControl position={position} $height={$height}>
        {children}
      </InnerSelectControl>
      {label && label}
    </SelectControlWraper>
  )
})

export const FlexBox = styled.div<{
  flexDirection?: 'row' | 'column'
  alignItems?: string
  justifyContent?: string
  gap?: string | number
}>`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: ${props => props.flexDirection || 'row'};
  align-items: ${props => props.alignItems || 'center'};
  justify-content: ${props => props.justifyContent || 'center'};
  gap: ${props => (typeof props.gap === 'number' ? `${props.gap}px` : props.gap || '8px')};
`

export const PlaceholderSpan = styled.span<{
  alignText?: string
  size?: keyof typeof Typography.sizes
  color?: keyof typeof Colors
}>`
  flex: 1;
  white-space: nowrap;
  color: ${props => (props.color ? Colors[props.color] : 'inherit')};
  text-align: ${props => props.alignText || 'left'};
  ${props => Typography.sizes[props.size || 'Tiny']};
`

export const Label = styled.label`
  ${Typography.sizes.Caption};
  white-space: nowrap;
`

export const GroupControl = styled(GroupControlComponent)`
  ${Media.Phone`
    ${GroupControlTitle} {
      text-align: center;
    }
  `}
`
